import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MapService } from './map.service';
import { StepsNavigationMapService } from './steps-navigation.service';
import { MatDialogService } from 'src/app/services/mat-dialog.service';
import { IndicatorsService } from './indicators.service';
import { RouteDataService } from './route-data.service';
import { ItemStatus } from '../interfaces/model';
import { Point, Transfers } from '../../../interfaces/custom-route.interface';
import { DebugService } from 'src/app/services/debug.service';
import dayjs from 'dayjs';
import { StandardDialog } from 'src/app/shared/dialogs';

@Injectable({ providedIn: 'root' })

export class RealTimeNavigationService {
    private _debugService = inject(DebugService);
    private _routeDataService: RouteDataService = inject(RouteDataService);
    private _mapService: MapService = inject(MapService);
    private _stepsNavigationService: StepsNavigationMapService = inject(StepsNavigationMapService);
    private _indicatorService: IndicatorsService = inject(IndicatorsService);
    private _matDialogService: MatDialogService = inject(MatDialogService);
    private _translateService: TranslateService = inject(TranslateService);

    private currentRouteDay = computed<Point[]>(() => this._routeDataService.getCurrentRouteDay());
    private currentDayIndex = computed<number>(() => this._routeDataService.currentDayIndex());
    private isGeolocationActive = computed<boolean>(() => this._routeDataService.isGeolocationActive());

    private watchId: number;
    private dialogRef: MatDialogRef<any>;

    geolocationEffect = effect(() => {
        const isGeolocationActive = this.isGeolocationActive();
        untracked(() => {
            this._debugService.info(isGeolocationActive, '[RealTimeNavigationService] geolocationEffect');
            if (isGeolocationActive)
                this.startWatching();
            else{
                this.stopWatching();
                this._stepsNavigationService.startStepRoute();
            }
        })
    }, { allowSignalWrites: true });

    startWatching(): void {
        this.watchId = navigator.geolocation.watchPosition(
            pos => {
                const userPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                if (!this.isGeolocationActive())
                    return;

                this.setLocationMarkerOnCurrentSegment(userPos);
            },
            err => {
                console.error('watchPosition error:', err)
                if (err.code === 1)
                    this._routeDataService.disableGeolocation();
            },
            { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
        );
    }

    stopWatching(): void {
        if (!this.watchId)
            return;

        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
    }

    public setLocationMarkerOnCurrentSegment(position: google.maps.LatLngLiteral): void {
        const MAX_DISTANCE: number = 100;
        const currentSegment: Point = this.currentRouteDay()?.find(s => s.status === ItemStatus.RUNNING)!;
        if (!currentSegment)
            return;

        const { distanceAlongPath, itemLocation, itemDistance, itemHeading } = this._routeDataService.getNearestItemInfo(position, currentSegment?.polylinesPath);
        if (itemDistance > MAX_DISTANCE) {
            console.warn("LOCATION METERS EXCEDED");
            this.showNoGPSDialog();
            this._indicatorService.resetInstructions();
            this._mapService.projectedPoint.set(undefined);
            return;
        }

        this.dialogRef = null;
        let segmentIndex: number = currentSegment?.transferPathDistances?.findIndex(td => distanceAlongPath < td);
        if (segmentIndex === -1) 
            segmentIndex = currentSegment.transfers?.length - 1;
        
        const currentStep: Transfers = currentSegment.transfers?.[segmentIndex];
        const mode: string = currentStep?.mode;
        const secondsRemaining: number = this.getTimeRemainig(mode, itemLocation, currentStep?.location);
        const progress: number = Math.round((segmentIndex / (currentSegment.transfers?.length - 1)) * 100);

        this._indicatorService.setInstructions(currentStep);
        this._indicatorService.setStepProgress(progress);
        this._indicatorService.setTimeRemainig(secondsRemaining);
        
        this._mapService.projectedPoint.set(itemLocation);
        this._mapService.projectedPointRotation.set(itemHeading);

        this._mapService.map.googleMap?.panTo(itemLocation);
        this._mapService.map.googleMap?.setZoom(18);
    }

    public getTimeRemainig(mode: string, itemLocation: google.maps.LatLngLiteral, segmentLocation: google.maps.LatLngLiteral): number{
        switch(mode){
            case 'Metro': return this.getTimeRemainingFromTwoPoints(itemLocation, segmentLocation, 8.33);
            case 'Bus': return this.getTimeRemainingFromTwoPoints(itemLocation, segmentLocation, 3.61);
            default: return this.getTimeRemainingFromTwoPoints(itemLocation, segmentLocation, 1.4);
        }
    }
        
    public getTimeRemainingFromTwoPoints(from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral, speedMps): number {
        try{
            const d = google.maps.geometry.spherical.computeDistanceBetween(
                new google.maps.LatLng(from.lat, from.lng),
                new google.maps.LatLng(to.lat, to.lng)
            );
            const seconds = d / speedMps;
            return Math.ceil(seconds / 60);
        }
        catch(e){
            return 0;
        }
    }

    private showNoGPSDialog(): void {
        if (this.dialogRef || !this.isGeolocationActive())
            return;

        this.dialogRef = this._matDialogService.openMatDialog({
            component: StandardDialog,
            data: {
                title: this._translateService.instant('Tenemos dificultades para ubicarte'),
                content: this._translateService.instant('Parece que hemos perdido momentáneamente tu posición. Puedes reintentarlo o si lo prefieres, puedes seguir con la ruta con el sistema de navegación paso a paso.'),
                primaryActionButtonText: this._translateService.instant('Continuar'),
                secondaryActionButtonText: this._translateService.instant('Reintentar'),
                handleClickPrimaryButton: () => {
                  this._routeDataService.disableGeolocation();                  
                  this._matDialogService.closeMatDialog(this.dialogRef);
                  setTimeout(() => {
                      this._stepsNavigationService.startRoute();
                  }, 150);
                },
                handleClickSecondaryButton: () => {
                  this._matDialogService.closeMatDialog(this.dialogRef);
                }
            }
        })

        this.dialogRef.afterClosed().subscribe(() => {
            setTimeout(() => {
                this.dialogRef = null;
            }, 18000);
        })
    }

    private currentHourEffect = effect( () => {
        const hour: dayjs.Dayjs = this._routeDataService.getCurrentTime();
        if (!this.isRealHour() || !this.isGeolocationActive() || this.isFakeGPSActive())
            return;

        this._debugService.info(!this.isRealHour() || !this.isGeolocationActive(), '[RealTimeNavigationService] currentHourEffect');
        this._routeDataService.setActiveRouteSegmentByHour(hour);
    }, { allowSignalWrites: true });

    //---- FOR TESTING PURPOSES -----------------------------------------------------------------
    public isFakeGPSActive = signal<boolean>(false);
    public manualHour = signal<dayjs.Dayjs>(dayjs().hour(9).minute(0));
    public canSetMarkerFromMap = signal<boolean>(false);
    public isRealHour = signal<boolean>(true);

    dayToShowIndexEffect = effect(() => {
        const day: number = this.currentDayIndex();
        this._debugService.info(day, '[RealTimeNavigationService] dayToShowIndexEffect');
        this.isFakeGPSActive.set(false);
    }, { allowSignalWrites: true });

    private currentManualHourForTestingPurposesEffect = effect( () => {
        const isRealHour = this.isRealHour()
        const hour = this.manualHour();
        const isFakeGPSActive = this.isFakeGPSActive();
        if (!isFakeGPSActive && !isRealHour){
            this.isRealHour.set(true);
            this.manualHour.set(dayjs().hour(9).minute(0));
        }

        untracked(() => {
            if (!isFakeGPSActive){
                this._debugService.info('1st noFakeGPSActive', '[RealTimeNavigationService] currentManualHourForTestingPurposesEffect ');
                this._indicatorService.resetInstructions();
                this.canSetMarkerFromMap.set(false);
                this._stepsNavigationService.startStepRoute();
                return;
            }

            if (this.isRealHour()){
                this._debugService.info('2nd isRealHour', '[RealTimeNavigationService] currentManualHourForTestingPurposesEffect');
                this._mapService.resetMarkerProjection();
                const currentTrameIndex = this._routeDataService.setActiveRouteSegmentByHour(this._routeDataService.getCurrentTime());
                this._mapService.fitCurrentTrameBounds(currentTrameIndex);
                return;
            }
            this._debugService.info('3rd Default', '[RealTimeNavigationService] currentManualHourForTestingPurposesEffect');
            this._mapService.resetMarkerProjection();
            const currentTrameIndex = this._routeDataService.setActiveRouteSegmentByHour(hour);
            this._mapService.fitCurrentTrameBounds(currentTrameIndex);
        })
    }, { allowSignalWrites: true });

}